import { getLogger } from '../logging';
import StructuredError, { ErrorCode } from '../model/structured-error';

const log = getLogger('promisesUtils');

/**
 * A deferred promise.  this contains the actual promise, as well as the resolve/reject functions needed to handle it.
 */
export class DeferredPromise<T> {
	constructor() {
		const self = this;
		this.resolve = () => {};
		this.reject = () => {};
		this.promise = new Promise<T>((resolve, reject) => {
			self.resolve = resolve;
			self.reject = reject;
		});
	}

	promise: Promise<T>;
	resolve: (result: T) => void;
	reject: (error: Error) => void;
}

/**
 * A promise that will only start calculating as soon as someone tries to use its resolved value.
 */
export class LazyPromise<T> implements Promise<T> {
	private provider: () => Promise<T>;
	private target: Promise<T> | null = null;

	constructor(provider: () => Promise<T>) {
		this.provider = provider;
	}

	then<TR = T, CR = never>(
		onfulfilled?: ((v: T) => PromiseLike<TR> | TR) | undefined | null,
		onrejected?: ((r: any) => PromiseLike<CR> | CR) | undefined | null,
	): Promise<TR | CR> {
		return this.getTarget().then(onfulfilled, onrejected);
	}

	catch<CR = never>(onrejected?: ((reason: any) => PromiseLike<CR> | CR) | undefined | null): Promise<T | CR> {
		return this.getTarget().catch(onrejected);
	}

	finally(onfinally?: (() => void) | undefined | null): Promise<T> {
		return this.getTarget().finally(onfinally);
	}

	private getTarget(): Promise<T> {
		if (this.target === null) this.target = this.provider();
		return this.target;
	}

	readonly [Symbol.toStringTag] = 'LazyPromise';
}

export type PollFunction = <T>(
	innerFunc: () => Promise<T>,
	until: (t: T) => Promise<boolean> | boolean,
	interval: number,
	timeout: number,
	description: string,
	abort?: () => boolean,
) => Promise<T>;

/**
 * Poll a function until the result of it fulfills a given condition.
 * @param polledFunc A function to poll.  This may return a promise.
 * @param until A function to validate whether the result of the first function, or of its resolved promise,
 *              can be considered a final answer.  This function must return true or false.
 * @param interval interval in milliseconds between two attempts
 * @param timeout timeout in milliseconds; after this the returned promise will be rejected
 * @param description a description of the thing we're polling for, for logging purposes.
 * @param abort A function to force stop polling
 * @return a promise of the poll; which will resolve to the final response of the polledFunc if it adhered
 *         to the condition, or reject if the polledFunc had an error or if there was no decent answer in
 *         time.
 */
export async function poll<T>(
	polledFunc: () => Promise<T>,
	until: (t: T) => Promise<boolean> | boolean,
	interval: number,
	timeout: number,
	description: string,
	abort: () => boolean = () => false,
): Promise<T> {
	const deadline = Date.now() + timeout;

	let pollResult: T = await polledFunc();
	while (!(await until(pollResult)) && !abort()) {
		if (Date.now() > deadline) throw new StructuredError(ErrorCode.POLL_TIMEOUT, `timeout on ${description}`);
		await sleep(interval);
		log.debug(`Polling ${description}`);
		pollResult = await polledFunc();
	}

	return pollResult;
}

/**
 * @return a promise that resolves after the given number of milliseconds.
 */
export function sleep(millis: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, millis);
	});
}

/**
 * @return a promise that never resolves.
 */
export function forever(): Promise<never> {
	return new Promise((_resolve) => {});
}

/** A special promise indicating that the value still needs to get a proper promise assigned.  If this promise is
 * resolved too soon, it will cause a reject.
 *
 * On a technical level: This is different from a straight-up Promise.reject because the latter will be caught by
 * the 'unhandled rejection' handler. */
export function uninitialized(message: string): Promise<never> {
	return new LazyPromise(() => Promise.reject(new Error(message)));
}

export function checkForSlowLoading<T>(promise: Promise<T>, timeoutMs: number, callback: Function) {
	callback(false);
	promise.finally(() => callback(false));
	Promise.race([promise, sleep(timeoutMs).then(() => 'slow')]).then((result) => {
		if (result === 'slow') callback(true);
	});
}
